import { PARTNERSHIP_4, partnerOf } from '../../core/seats.js';
import { DEFAULT_FH_CONFIG, FH_TRICKS_PER_HAND, validateFhConfig, } from './config.js';
import { fhDeck, fhDealPlan } from './deck.js';
import { fhAuction } from './auction.js';
import { fhRanking } from './ranking.js';
import { fhCurrentTrick, fhLedSuitOf, fhLegalPlays } from './legality.js';
import { fhApplyDecision, fhInitHandExtra, fhOnContract } from './decisions.js';
import { fhMatchEnd, fhScoreHand } from './scoring.js';
import { fhVisibility } from './visibility.js';
import { isMisereMode, } from './types.js';
function addVoid(voids, seat, suit) {
    const current = voids[seat] ?? [];
    if (current.includes(suit))
        return voids;
    return voids.map((v, i) => (i === seat ? [...v, suit] : v));
}
/**
 * Records everything the rest of the module reads back out of `extra`.
 *
 * Written to be correct whether the reducer calls it before or after appending
 * the play to the trick, because `core/module.ts` does not say which.
 */
export function fhOnCardPlayed(hand, seat, card, nominate, config) {
    const extra = hand.extra;
    const contract = hand.contract;
    if (!contract)
        return extra;
    const trick = fhCurrentTrick(hand);
    const alreadyRecorded = trick.plays.some((p) => p.seat === seat && p.card === card);
    const isLead = alreadyRecorded
        ? trick.plays[0]?.seat === seat && trick.plays[0]?.card === card
        : trick.plays.length === 0;
    const ctx = fhRanking(contract, extra, config);
    let next = extra;
    if (isLead) {
        const nominatedLead = ctx.effectiveSuit(card) === null ? nominate ?? null : null;
        if (nominatedLead !== null) {
            // Rules §4.3: leading the floating Joker names the suit everyone else
            // must follow. The core cannot derive this from the card alone.
            next = {
                ...next,
                leadNominations: next.leadNominations.map((s, i) => (i === trick.index ? nominatedLead : s)),
            };
        }
        const led = ctx.effectiveSuit(card) ?? nominatedLead;
        if (led !== null && !next.suitsLed.includes(led)) {
            // Gates leading the Joker later in the hand (edge #6).
            next = { ...next, suitsLed: [...next.suitsLed, led] };
        }
    }
    else {
        const led = trick.ledSuit ?? next.leadNominations[trick.index] ?? null;
        if (led !== null && ctx.effectiveSuit(card) !== led) {
            // Public, perfect information the instant it happens (§10.4). Kept in
            // hand state, not only in the bot's inference, because the optional
            // "cannot call a suit you reneged on" rule is a legality question.
            next = { ...next, revealedVoids: addVoid(next.revealedVoids, seat, led) };
        }
    }
    return next;
}
export const fiveHundred = {
    meta: { id: 'five-hundred', name: '500', rotation: 'clockwise' },
    defaultConfig: DEFAULT_FH_CONFIG,
    validateConfig: validateFhConfig,
    table: (_config) => PARTNERSHIP_4,
    tricksPerHand: (_config) => FH_TRICKS_PER_HAND,
    deck: (config) => fhDeck(config),
    dealPlan: (config) => fhDealPlan(config),
    auction: fhAuction,
    ranking(hand, config) {
        return fhRanking(hand.contract, hand.extra, config);
    },
    legalPlays(hand, seat, _table, config) {
        return fhLegalPlays(hand, seat, config);
    },
    initHandExtra(_config) {
        return fhInitHandExtra(PARTNERSHIP_4.seatCount);
    },
    initMatchExtra(_config) {
        return { lastContract: null };
    },
    onContract(hand, table, config) {
        return fhOnContract(hand, table, config);
    },
    applyDecision(hand, seat, decision, table, config) {
        return fhApplyDecision(hand, seat, decision, table, config);
    },
    onCardPlayed(hand, seat, card, nominate, config) {
        return fhOnCardPlayed(hand, seat, card, nominate, config);
    },
    ledSuitOf(hand, card, nominate, config) {
        return fhLedSuitOf(hand, card, nominate, config);
    },
    /** Rules §5.2: the winner of each trick leads to the next. (The contractor
     *  leads trick 1; that is set by onContract's `leader`.) */
    nextLeader(_hand, winner, _table, _config) {
        return winner;
    },
    scoreHand(hand, table, config) {
        return fhScoreHand(hand, table, config);
    },
    /** Rules §3.1.6 / edge #14: all four pass ⇒ throw the hand in and redeal.
     *  NOTE: the recommended default also moves the deal to the next dealer, and
     *  the core's return type has no way to say "same dealer", so
     *  `config.allPassBehaviour: 'sameDealer'` cannot currently be honoured. */
    onPassedOut(_config) {
        return { kind: 'redeal' };
    },
    matchEnd(state, config) {
        return fhMatchEnd(state, config);
    },
    roles(hand, table, _config) {
        const contract = hand.contract;
        const sittingOut = hand.extra?.sittingOut ??
            (contract && isMisereMode(contract.mode) ? partnerOf(table, contract.contractor) : null);
        const all = Array.from({ length: table.seatCount }, (_, i) => i);
        return {
            activeSeats: sittingOut === null ? all : all.filter((s) => s !== sittingOut),
            // 500 has no dummy: everybody plays their own cards.
            controllerOf: all,
        };
    },
    visibility: fhVisibility,
    contractLabel(contract, _config) {
        return contract ? contract.label : 'Passed out';
    },
};
//# sourceMappingURL=module.js.map